import { useEffect } from 'react';

import { useRouter } from 'next/router';

import { useQuery } from '@tanstack/react-query';

import { isEqual } from 'lodash';
import { useTranslation } from 'next-export-i18n';
import { useSnackbar } from 'notistack';

import {
  addParty,
  getPaymentLinkById,
  getStatus,
  saveCustomerData,
} from '@/services';
import { useStore } from '@/store';
import {
  BillStatusEnum,
  CurrentStatuses,
  IPaymentStatus,
  LinkStatus,
  PaymentLinkEffectProps,
  PaymentLinkStatusEffectProps,
  UseSaveCustomerDataEffectProps,
} from '@/types';
import { useQlubRouter } from '@clubpay/customer-common-module/src/hook/router';
import {
  getDiningSession,
  getSession,
  removeDiningSession,
  setDiningSession,
} from '@clubpay/customer-common-module/src/service/session/dsi';

const PREV_PAYMENT_STATUS = 'PREV_PAYMENT_STATUS';

let init = false;
let shouldGoAhead = true;

export function usePaymentLink(props?: PaymentLinkEffectProps) {
  const { url } = useQlubRouter();
  const cc = url.cc;
  const slug = url.slug;
  const orderId = url.id;
  const hash = url.hash;

  const toggleLoader = useStore(state => state.toggleLoader);
  const setCurrentStatus = useStore(state => state.setCurrentStatus);

  const checkPrevPaymentStatus = () => {
    try {
      const prevPaymentStatus = JSON.parse(
        sessionStorage.getItem(PREV_PAYMENT_STATUS) || '{}',
      ) as IPaymentStatus;
      if (prevPaymentStatus?.bill?.status === BillStatusEnum.fullyPaid) {
        removeDiningSession(url);
        sessionStorage.removeItem(PREV_PAYMENT_STATUS);
      }
    } catch {
      // do nothing
    }
  };

  const query = useQuery({
    queryKey: ['payment-link-details', cc, slug, orderId, hash],
    queryFn: () => {
      checkPrevPaymentStatus();
      const existingDiningSession = getDiningSession(url);
      if (props?.shouldUpdateLoader) toggleLoader({ linkDetails: true });
      return getPaymentLinkById({
        cc,
        restaurantUnique: slug,
        orderId,
        hash,
        diningSessionID: existingDiningSession?.id,
      }).then(async res => {
        if (!existingDiningSession?.id)
          setDiningSession(url, { id: res.diningSessionID, jwt: res.JWTToken });
        const diningSession = getDiningSession(url);
        // in multi-use payment links, new dining sessions are created for every payment

        if (props?.shouldUpdateStatus) {
          if (res && (res.expired || res.expiration < Date.now()))
            setCurrentStatus(CurrentStatuses.EXPIRED);
          else if (res && res.status === LinkStatus.DISCARDED)
            setCurrentStatus(CurrentStatuses.DISCARDED);
          else if (
            res &&
            !res.isMultiUse &&
            (res.status === LinkStatus.PAID || Number(res.bill.remaining) <= 0)
          )
            setCurrentStatus(CurrentStatuses.PAID);
        }

        if (!init) {
          init = true;
          if (res.diningSessionID !== '') {
            await addParty({
              id: getSession(),
              diningSessionID: diningSession?.id || '',
              country: cc,
              restaurantUnique: slug,
            });
          }
        }

        if (props?.shouldUpdateLoader) toggleLoader({ linkDetails: false });
        return res;
      });
    },
    refetchOnWindowFocus: false,
    enabled: !!cc && !!slug,
  });

  return query;
}

export function usePaymentLinkStatus({
  reference = '',
  enabled = true,
}: PaymentLinkStatusEffectProps) {
  const { url } = useQlubRouter();
  const dsi = getDiningSession(url)?.id || '';
  const id = getSession();

  const query = useQuery({
    queryKey: ['payment-status', dsi, id, reference],
    queryFn: async () => {
      const statusRes = await getStatus({
        diningSessionID: dsi,
        id,
        reference: reference,
      });
      sessionStorage.setItem(PREV_PAYMENT_STATUS, JSON.stringify(statusRes));
      return statusRes;
    },
    refetchOnWindowFocus: false,
    networkMode: 'offlineFirst',
    enabled: !!reference && !!dsi && enabled,
    retry: false,
  });

  return query;
}

export function useCheckLinkUpdates() {
  const { t } = useTranslation();
  const { lang } = useRouter().query;
  const { lang: qlang } = useQlubRouter();
  const { enqueueSnackbar } = useSnackbar();
  const { data } = usePaymentLink();
  const setInitialLinkData = useStore(state => state.setInitialLinkData);
  const initialLink = useStore(state => state.initialLink);

  useEffect(() => {
    if (shouldGoAhead && data && (lang === qlang || lang === undefined)) {
      const d = {
        billAmount: data.bill.billAmount!,
        reference: data.reference || '',
        description: data.description || '',
      };
      if (
        initialLink?.sequentialID === data.sequentialID &&
        !isEqual(initialLink?.data, d)
      )
        enqueueSnackbar(
          t(
            'details-of-the-payment-link-was-modified-please-review-and-proceed',
          ),
          {
            variant: 'info',
            autoHideDuration: 5000,
          },
        );
      setInitialLinkData({
        sequentialID: data.sequentialID!,
        data: d,
      });
      shouldGoAhead = false;
    }
  }, [data, lang, qlang]);
}

export function useSaveCustomerData({
  ref,
  enabled = true,
}: UseSaveCustomerDataEffectProps) {
  const { url } = useQlubRouter();
  const dsi = getDiningSession(url)?.id || '';
  const userInfo = useStore(state => state.userInfo);
  const setUserInfo = useStore(state => state.setUserInfo);

  const handleSaveCustomerData = async (reference: string) =>
    await saveCustomerData({
      dsi,
      email: userInfo?.email,
      phone: userInfo?.phone,
      firstName: userInfo?.firstName,
      lastName: userInfo?.lastName,
      reference,
    });

  useEffect(() => {
    if (
      ref &&
      (userInfo?.email ||
        userInfo?.phone ||
        (userInfo?.firstName && userInfo?.lastName)) &&
      !userInfo?.saved &&
      enabled
    ) {
      handleSaveCustomerData(ref).then(() => {
        if (userInfo) setUserInfo({ ...userInfo, saved: true });
      });
    }
  }, [ref, enabled]);

  useEffect(() => {
    if (userInfo && dsi && userInfo.dsi !== dsi) setUserInfo({ dsi });
  }, []);
}
